import {Accordion, AccordionDetails, AccordionSummary, Card, Grid} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {SelectInputElement} from '../Components/SelectInputElement';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {HealthQuestionsProps} from './HealthQuestionsList';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/HealthXref';

const PsychologicalDisorder = observer(({counter, valueChanged, parameters, coreQuestions}: HealthQuestionsProps) => {
    return (
        <Accordion style={{width: '100%'}} TransitionProps={{unmountOnExit: true}}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={'psychologicalDisorder-content'}
                id={'psychologicalDisorder-header'}
            >
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <div>Psychological disorder</div>
                    <div>{counter('psychologicalDisorder')}</div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid key="params" container>
                        {coreQuestions?.map(({name, label}) => (
                            <Grid item xs={4} key={name}>
                                <SwitchSegmentedYesNo
                                    param={{
                                        name: name,
                                        label: label,
                                        category: 'psychologicalDisorder',
                                        ...(parameters[name as keyof typeof parameters] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <SelectInputElement
                                param={{
                                    label: 'How many medications are you taking for the psychological conditions above?',
                                    name: 'psychNumMedications',
                                    options: fieldOptions.psychNumMedications.options,
                                    category: 'psychologicalDisorder',
                                    ...parameters.psychNumMedications,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SwitchSegmentedYesNo
                                param={{
                                    name: 'psychHospitalization',
                                    label: 'Have you been hospitalized due to the psychological conditions above?',
                                    category: 'psychologicalDisorder',
                                    ...parameters.psychHospitalization,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        {parameters.Anxiety.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Anxiety</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label={'When were you diagnosed?'}
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.anxietyDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.anxietyDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'What is the severity?',
                                                    name: 'anxietySeverity',
                                                    options: fieldOptions.anxietySeverity.options,
                                                    ...parameters.anxietySeverity,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'anxietyMedications'}
                                                label={
                                                    'How many medications are you currently prescribed to treat anxiety?'
                                                }
                                                value={parameters.anxietyMedications?.value}
                                                onChange={(value) => valueChanged(parameters.anxietyMedications, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'anxietyHospilization',
                                                    label: 'Have you been hospitalised due to anxiety?',
                                                    ...parameters.anxietyHospilization,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'anxietyTreatment'}
                                                label={'What treatment was prescribed or completed for this condition?'}
                                                value={parameters.anxietyTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.anxietyTreatment, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'anxietySuicideAttempts',
                                                    label: 'Have you ever been hospitalized due to this condition, attempted suicide or had suicidal thoughts?',
                                                    ...parameters.anxietySuicideAttempts,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Depression.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Depression</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label={'When were you diagnosed?'}
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.depressionDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.depressionDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'What is the severity?',
                                                    name: 'depressionSeverity',
                                                    options: fieldOptions.depressionSeverity.options,
                                                    ...parameters.depressionSeverity,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'depressionHospitalizations',
                                                    label: 'Have you been hospitalised due to depression?',
                                                    ...parameters.depressionHospitalizations,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'depressionTreatment'}
                                                label={'What treatment was prescribed or completed for this condition?'}
                                                value={parameters.depressionTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.depressionTreatment, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'depressionInpatient',
                                                    label: 'Have you ever been referred for or received inpatient treatment for this condition?',
                                                    ...parameters.depressionInpatient,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'depressionSuicideAttempts',
                                                    label: 'Have you ever been hospitalized due to this condition, attempted suicide or had suicidal thoughts?',
                                                    ...parameters.depressionSuicideAttempts,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.BipolarDisorder.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Bipolar Disorder</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label={'When were you diagnosed?'}
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.bipolarDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.bipolarDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'bipolarMedications'}
                                                label={'How many medications are you currently prescribed?'}
                                                value={parameters.bipolarMedications?.value}
                                                onChange={(value) => valueChanged(parameters.bipolarMedications, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'bipolarYearsStable'}
                                                label={'How long have you been stable on medication?'}
                                                value={parameters.bipolarYearsStable?.value}
                                                onChange={(value) => valueChanged(parameters.bipolarYearsStable, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'bipolarHospitalizations',
                                                    label: 'Ever been hospitalized for this?',
                                                    ...parameters.bipolarHospitalizations,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Schizophrenia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Schizophrenia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label={'When were you diagnosed?'}
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.schizophreniaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.schizophreniaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'schizophreniaMedications'}
                                                label={'What medications are you currently prescribed?'}
                                                value={parameters.schizophreniaMedications?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.schizophreniaMedications, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'schizophreniaYearsStable'}
                                                label={'How long have you been stable on medication?'}
                                                value={parameters.schizophreniaYearsStable?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.schizophreniaYearsStable, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'schizophreniaHospitalizations',
                                                    label: 'Ever been hospitalized for this?',
                                                    ...parameters.schizophreniaHospitalizations,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.ADDADHD.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>ADD/ADHD</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.addDiagnosisDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.addDiagnosisDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'addYearsStable'}
                                                label="How long have you been stable on current medications?"
                                                value={parameters.addYearsStable?.value}
                                                onChange={(value) => valueChanged(parameters.addYearsStable, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'addSideEffects',
                                                    label: 'Any history of missed time from work or school, hospitalization, suicidal gestures attempts?',
                                                    ...parameters.addSideEffects,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="addTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.addTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.addTreatment, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.AutismSpectrumDisorder.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Autism Spectrum Disorder</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Autism Spectrum Disorder Severity',
                                                    name: 'autismSpectrumDisorderSeverity',
                                                    options: fieldOptions.autismSpectrumDisorderSeverity.options,
                                                    ...parameters.autismSpectrumDisorderSeverity,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
});

export default PsychologicalDisorder;
